import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { registerSW } from "virtual:pwa-register";
import { api, ApiError } from "./lib/api";
import type { Session } from "./lib/types";
import { AppShell } from "./components/AppShell";
import { LoadingState } from "./components/ui";
import { LoginPage } from "./features/auth/LoginPage";
import { AccountPage, SetPasswordPage } from "./features/auth/AccountPages";
import { SimpleCostingDashboard } from "./features/simple-costing/SimpleCostingDashboard";
import { SimpleProductEditorPage } from "./features/simple-costing/SimpleProductEditorPage";
import { SimpleProductDetailPage } from "./features/simple-costing/SimpleProductDetailPage";
import { UserManagementPage } from "./features/admin/UserManagementPage";
import "./index.css";

registerSW({ immediate: true });

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 2, refetchOnWindowFocus: false },
    mutations: { retry: false }
  }
});

function AuthGate() {
  const location = useLocation();
  const session = useQuery({ queryKey: ["session"], queryFn: () => api<Session>("/auth/session"), retry: false });
  if (session.isLoading) return <div className="grid min-h-dvh place-items-center"><LoadingState label="Opening Mist Mountain" /></div>;
  if (session.error instanceof ApiError && session.error.status === 401) return <Navigate to="/login" state={{ from: location }} replace />;
  if (!session.data) return <Navigate to="/login" replace />;
  return <AppShell session={session.data} />;
}

function AnonymousOnly() {
  const session = useQuery({ queryKey: ["session"], queryFn: () => api<Session>("/auth/session"), retry: false });
  if (session.isLoading) return <div className="grid min-h-dvh place-items-center"><LoadingState /></div>;
  return session.data ? <Navigate to="/" replace /> : <Outlet />;
}

function AdministratorOnly() {
  const session = useQuery({ queryKey: ["session"], queryFn: () => api<Session>("/auth/session"), retry: false });
  if (session.isLoading) return <LoadingState label="Checking access" />;
  return session.data?.roles.includes("Administrator") ? <Outlet /> : <Navigate to="/" replace />;
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route element={<AnonymousOnly />}><Route path="/login" element={<LoginPage />} /></Route>
          <Route path="/set-password" element={<SetPasswordPage />} />
          <Route element={<AuthGate />}>
            <Route index element={<SimpleCostingDashboard />} />
            <Route path="products/new" element={<SimpleProductEditorPage />} />
            <Route path="products/:id" element={<SimpleProductDetailPage />} />
            <Route path="products/:id/edit" element={<SimpleProductEditorPage />} />
            <Route path="account" element={<AccountPage />} />
            <Route element={<AdministratorOnly />}><Route path="users" element={<UserManagementPage />} /></Route>
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>
);
